import { useEffect, useState, useCallback, useRef } from "react";
import { ArrowLeft, X, ChevronLeft, ChevronRight, Maximize2, UploadCloud, ImagePlus, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const FILTERS = ["All Events", "Cultural Festival", "Community", "Arts", "Celebration", "Panel"];

export default function Gallery() {
  const [albums, setAlbums] = useState([]);
  const [filter, setFilter] = useState("All Events");
  const [active, setActive] = useState(null); // album object
  const [lightbox, setLightbox] = useState(null); // index

  useEffect(() => {
    api.get("/albums").then((r) => setAlbums(r.data)).catch(() => {});
  }, []);

  const shown = albums.filter((a) => filter === "All Events" || a.tag === filter);
  const totalPhotos = albums.reduce((s, a) => s + a.count, 0);

  const openAlbum = (a) => {
    setActive(a);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const backToAlbums = () => {
    setActive(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeLb = useCallback(() => setLightbox(null), []);
  const nextLb = useCallback(() => {
    if (!active) return;
    setLightbox((i) => (i + 1) % active.photos.length);
  }, [active]);
  const prevLb = useCallback(() => {
    if (!active) return;
    setLightbox((i) => (i - 1 + active.photos.length) % active.photos.length);
  }, [active]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLb();
      if (e.key === "ArrowRight") nextLb();
      if (e.key === "ArrowLeft") prevLb();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, closeLb, nextLb, prevLb]);

  return (
    <div data-testid="gallery-page">
      {/* HERO */}
      <section className="bg-ink crosshatch radial-glow pt-[68px]" data-testid="gallery-hero">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-[1.3fr_0.7fr] gap-12 items-center relative z-10">
          <div>
            <span className="eyebrow">Community Gallery</span>
            <h1 className="font-serif-eth font-light text-white mt-6 text-5xl lg:text-[68px] leading-[1.03]">
              Every moment we've <br /><em>built together.</em>
            </h1>
            <p className="font-body-eth text-white/60 text-lg mt-6 max-w-lg">
              A living archive of Ethos Foundation events — organized by album.
            </p>
          </div>
          <div className="hidden md:grid grid-cols-3 gap-4">
            {[
              { v: albums.length || 5, l: "Albums" },
              { v: totalPhotos || 28, l: "Photos" },
              { v: "24–25", l: "Events" },
            ].map((s) => (
              <div key={s.l} className="border border-white/12 bg-white/[0.03] px-4 py-6 text-center" style={{ borderRadius: 2 }}>
                <div className="font-serif-eth text-4xl text-glow">{s.v}</div>
                <div className="font-body-eth text-[11px] uppercase tracking-[0.16em] text-white/50 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {!active ? (
        <>
        <section className="bg-cream" data-testid="album-grid-view">
          <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
            {/* FILTER BAR */}
            <div className="flex flex-wrap gap-3 mb-10" data-testid="filter-bar">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setFilter(f)}
                  className={`chip font-body-eth text-sm font-medium px-5 py-2.5 ${filter === f ? "active" : ""}`}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* ALBUM GRID */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {shown.map((a, i) => (
                <Reveal key={a.id} delay={i * 90}>
                  <div className="group eth-card bg-white border border-[var(--line)] overflow-hidden" data-testid={`album-card-${a.slug}`}>
                    <div className="relative zoom-wrap h-[220px]">
                      <img src={a.cover} alt={a.name} loading="lazy" className="w-full h-full object-cover" />
                      <span className="absolute top-3 left-3 font-body-eth text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 text-white" style={{ background: a.color, borderRadius: 2 }}>{a.tag}</span>
                      <span className="absolute top-3 right-3 font-body-eth text-[11px] font-medium px-2.5 py-1 text-white" style={{ background: "rgba(10,22,40,0.7)", borderRadius: 2 }}>{a.count} photos</span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-serif-eth text-[20px] text-ink leading-tight">{a.name}</h3>
                      <div className="font-body-eth text-sm text-mute mt-1">{a.date}</div>
                      <button
                        onClick={() => openAlbum(a)}
                        data-testid={`view-photos-${a.slug}`}
                        className="btn-navy w-full mt-5 font-body-eth text-sm font-semibold py-3"
                      >
                        View Photos
                      </button>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        <SubmitSection albums={albums} />
        </>
      ) : (
        <section className="bg-cream" data-testid="album-photo-view">
          <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
            <button onClick={backToAlbums} data-testid="back-to-albums" className="inline-flex items-center gap-2 font-body-eth text-sm font-semibold text-teal hover:text-mid transition-colors mb-8">
              <ArrowLeft size={16} /> Back to Albums
            </button>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-body-eth text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 text-white" style={{ background: active.color, borderRadius: 2 }}>{active.tag}</span>
            </div>
            <h2 className="font-serif-eth font-light text-ink text-4xl lg:text-5xl">{active.name}</h2>
            <div className="font-body-eth text-mute mt-2">{active.date} · {active.count} photos</div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-10">
              {active.photos.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(i)}
                  data-testid={`photo-thumb-${i}`}
                  className="group relative zoom-wrap block"
                  style={{ borderRadius: 2, aspectRatio: "4 / 3" }}
                >
                  <img src={p.url} alt={p.caption} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center bg-ink/0 group-hover:bg-ink/45 transition-colors duration-300">
                    <Maximize2 size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LIGHTBOX */}
      {lightbox !== null && active && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: "rgba(5,10,18,0.95)" }}
          onClick={closeLb}
          data-testid="lightbox"
        >
          <button onClick={closeLb} data-testid="lightbox-close" className="absolute top-5 right-5 text-white/80 hover:text-white z-10">
            <X size={30} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); prevLb(); }} data-testid="lightbox-prev" className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 text-white/70 hover:text-white border border-white/20 hover:border-glow w-12 h-12 items-center justify-center" style={{ borderRadius: 2 }}>
            <ChevronLeft size={26} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); nextLb(); }} data-testid="lightbox-next" className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 text-white/70 hover:text-white border border-white/20 hover:border-glow w-12 h-12 items-center justify-center" style={{ borderRadius: 2 }}>
            <ChevronRight size={26} />
          </button>

          <div className="px-6 max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
            <img src={active.photos[lightbox].url} alt={active.photos[lightbox].caption} className="max-h-[80vh] max-w-full object-contain mx-auto" style={{ borderRadius: 2 }} />
            <div className="mt-5 flex items-center justify-between gap-4">
              <div className="font-body-eth text-white/70 text-sm">
                <span className="text-glow font-medium">{active.name}</span>
                {active.photos[lightbox].caption ? ` · ${active.photos[lightbox].caption}` : ""}
              </div>
              <div className="font-body-eth text-white/50 text-sm" data-testid="lightbox-counter">
                {lightbox + 1} of {active.photos.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const ALBUM_NAMES = [
  "African Festival of Boston",
  "Curl Bar Block Party 2025",
  "Arts in the Park 2025",
  "EMBRACE Boston 2025",
  "Layered Conversations 2025",
  "Other / New Event",
];

function SubmitSection({ albums }) {
  const names = (albums && albums.length ? albums.map((a) => a.name) : []).concat("Other / New Event");
  const options = names.length > 1 ? names : ALBUM_NAMES;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [album, setAlbum] = useState("");
  const [caption, setCaption] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (list) => {
    const imgs = Array.from(list).filter((f) => f.type.startsWith("image/"));
    setFiles((cur) => [...cur, ...imgs].slice(0, 10));
  };
  const removeFile = (idx) => setFiles((cur) => cur.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast.error("Please attach at least one photo");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("name", name);
    fd.append("email", email);
    fd.append("album", album || options[0]);
    fd.append("caption", caption);
    files.forEach((f) => fd.append("files", f));
    try {
      await api.post("/gallery/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Thank you! Your photos were submitted for review.");
      setName(""); setEmail(""); setAlbum(""); setCaption(""); setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed, please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-ink crosshatch radial-glow" data-testid="submit-photos-section">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-24 relative z-10 grid lg:grid-cols-[0.85fr_1.15fr] gap-14 items-start">
        <div>
          <span className="eyebrow">Share Your Photos</span>
          <h2 className="font-serif-eth font-light text-white text-4xl lg:text-5xl mt-5 leading-tight">
            Were you <em>there?</em> Add your <em>moment.</em>
          </h2>
          <p className="font-body-eth text-white/60 leading-relaxed mt-5 max-w-md">
            Help us build the archive. Submit photos from any Ethos Foundation event and, once reviewed, they may join the community gallery.
          </p>
          <div className="mt-8 flex items-start gap-3 border-t border-white/10 pt-6">
            <ImagePlus size={22} className="text-glow shrink-0 mt-0.5" />
            <div className="font-body-eth text-sm text-white/55">
              Up to 10 images · JPG, PNG, WEBP or GIF · 12MB each
            </div>
          </div>
        </div>

        {done ? (
          <div className="bg-ink2 border border-white/10 p-10 text-center" style={{ borderRadius: 2 }} data-testid="submit-success">
            <div className="w-14 h-14 mx-auto flex items-center justify-center bg-teal" style={{ borderRadius: 2 }}>
              <Check size={26} className="text-white" />
            </div>
            <h3 className="font-serif-eth text-3xl text-white mt-6">Photos <em>received.</em></h3>
            <p className="font-body-eth text-white/60 text-sm mt-3">Thank you for helping preserve our community's story. We'll review your submission shortly.</p>
            <button onClick={() => setDone(false)} data-testid="submit-another" className="btn-teal font-body-eth text-sm font-semibold px-6 py-3 mt-7">Submit more photos</button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-ink2 border border-white/10 p-7 lg:p-9" style={{ borderRadius: 2 }} data-testid="submit-photos-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-body-eth text-[11px] uppercase tracking-wider text-white/45">Name</label>
                <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="submit-name" className="eth-input w-full py-2.5 text-white mt-1" style={{ borderBottomColor: "rgba(255,255,255,0.18)" }} />
              </div>
              <div>
                <label className="font-body-eth text-[11px] uppercase tracking-wider text-white/45">Email</label>
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="submit-email" className="eth-input w-full py-2.5 text-white mt-1" style={{ borderBottomColor: "rgba(255,255,255,0.18)" }} />
              </div>
            </div>

            <div className="mt-5">
              <label className="font-body-eth text-[11px] uppercase tracking-wider text-white/45">Event / Album</label>
              <select value={album} onChange={(e) => setAlbum(e.target.value)} data-testid="submit-album" className="eth-input w-full py-2.5 text-white mt-1 bg-transparent" style={{ borderBottomColor: "rgba(255,255,255,0.18)" }}>
                {options.map((o) => <option key={o} value={o} className="text-ink">{o}</option>)}
              </select>
            </div>

            <div className="mt-5">
              <label className="font-body-eth text-[11px] uppercase tracking-wider text-white/45">Caption (optional)</label>
              <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="A few words about these photos" data-testid="submit-caption" className="eth-input w-full py-2.5 text-white mt-1" style={{ borderBottomColor: "rgba(255,255,255,0.18)" }} />
            </div>

            {/* Dropzone */}
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              className="mt-6 border border-dashed border-white/25 hover:border-glow transition-colors cursor-pointer p-8 text-center"
              style={{ borderRadius: 2 }}
              data-testid="submit-dropzone"
            >
              <UploadCloud size={28} className="text-glow mx-auto" />
              <div className="font-body-eth text-white/70 text-sm mt-3">Click to choose photos or drag & drop</div>
              <input ref={inputRef} type="file" accept="image/*" multiple hidden data-testid="submit-files" onChange={(e) => addFiles(e.target.files)} />
            </div>

            {files.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 mt-5" data-testid="submit-previews">
                {files.map((f, i) => (
                  <div key={i} className="relative group" style={{ aspectRatio: "1 / 1" }}>
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" style={{ borderRadius: 2 }} />
                    <button type="button" onClick={() => removeFile(i)} className="absolute -top-2 -right-2 bg-ink border border-white/20 text-white w-6 h-6 flex items-center justify-center" style={{ borderRadius: 2 }}>
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button type="submit" disabled={busy} data-testid="submit-photos-btn" className="btn-gold w-full font-body-eth text-sm font-semibold py-3.5 mt-7 disabled:opacity-60">
              {busy ? "Uploading..." : `Submit ${files.length || ""} Photo${files.length === 1 ? "" : "s"}`.trim()}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
